import { type IconProps } from '@loadsmart/miranda-react'

import { formatDate, parse } from 'core/utils/date'

import {
  type ShipperAttachment,
  type AttachmentDocumentType,
  getCertificateExpirationStatus,
} from '../domain/Document'

const documentTypeLabels: Record<AttachmentDocumentType, string> = {
  certificate_of_insurance: 'COI',
  w9: 'W9',
  other: 'Other',
  client_contract: 'Client Contract',
}

function getDocumentTypeLabel(documentType: AttachmentDocumentType): string {
  return documentTypeLabels[documentType]
}

function formatExpirationDate(expirationDate: string): string {
  return formatDate(parse(expirationDate, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')
}

function getExpirationLabelSuffix(attachment: ShipperAttachment): string {
  if (!attachment.expirationDate) {
    return ''
  }

  const formattedExpirationDate = formatExpirationDate(attachment.expirationDate)
  const expirationStatus = getCertificateExpirationStatus(attachment)

  if (expirationStatus === 'expired') {
    return `expired: ${formattedExpirationDate}`
  }

  if (expirationStatus === 'expiring_soon') {
    return `expiring soon: ${formattedExpirationDate}`
  }

  return `expire date: ${formattedExpirationDate}`
}

export function formatShipperAttachmentLabel(attachment: ShipperAttachment): string {
  const { documentType, expirationDate } = attachment
  const typeLabel = getDocumentTypeLabel(documentType)

  if (!expirationDate) {
    return typeLabel
  }

  const expirationLabelSuffix = getExpirationLabelSuffix(attachment)

  return `${typeLabel} (${expirationLabelSuffix})`
}

export function getCertificateExpirationIndicator(
  attachment: Pick<ShipperAttachment, 'documentType' | 'expirationDate'>
): { icon: IconProps['name']; color: IconProps['color'] } | null {
  const expirationStatus = getCertificateExpirationStatus(attachment)

  if (expirationStatus === 'expired') {
    return { icon: 'attention', color: 'color-danger-60' }
  }

  if (expirationStatus === 'expiring_soon') {
    return { icon: 'clock', color: 'color-text-highlight' }
  }

  return null
}
